import { Component, inject, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { format } from 'date-fns';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../auth.service';
import { FormConfigService } from '../registration/forms-config.service';
import { AppStateService } from '../app-state.service';
import { CommonModule, DatePipe } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { DefaultComponent } from '../default/default.component';
import { MaterialModule } from '../material.module';
import { OtpAuthComponent } from '../otp-auth/otp-auth.component';
import { CustomValidators } from './customer-validators';
import { AutoSlashDateFormatDirective } from '../auto-slash-date-format.directive';
import { environment } from '../../environments/environment';
import { ConfigService } from '../config.service';
import { NewPatientComponent } from "../registration/new-patient/new-patient.component";
import { HeaderComponent } from '../header/header.component';
import { ClinicInformation } from '../registration/clinic.model';
import { StorageEncryptionUtil } from '../utils/storage-encryption.util';
import { RememberMeInfoDialogComponent } from './remember-me-info-dialog.component';
import { PatientSelectorDialogComponent } from './patient-selector-dialog.component';
import { AppointmentService } from '../registration/appointment.service';
import { DeeplinkService } from '../deeplink.service';


@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    MatDatepickerModule,
    MatNativeDateModule,
    DefaultComponent,
    OtpAuthComponent,
    AutoSlashDateFormatDirective,
    HeaderComponent,
    RouterLink
],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [DatePipe]
})
export class LoginComponent implements OnInit, AfterViewInit {
  @ViewChild('phoneInput') phoneInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dobInput') dobInput!: ElementRef<HTMLInputElement>;
  clinicInfo: ClinicInformation = {} as ClinicInformation;
  authForm: FormGroup;
  requestInProgress: boolean = false;
  clientId: string = '';
  orgId: string = '';
  patientId: string = '';
  otpVerifyInProgress: boolean = false;
  showOtpInput: boolean = false;
  phoneNumberForOtp: string = '';
  debug = environment.debug;
  appConfig : any;
  configService = inject(ConfigService);
  authService = inject(AuthService);
  isNewPatient = false;
  redirectUrl = '';

  // Name verification for multiple patients with same phone + DOB
  showNameVerification: boolean = false;
  pendingPatients: any[] = [];
  pendingPhoneNumber: string = '';
  nameVerificationForm!: FormGroup;
  nameVerificationError: string = '';

  private readonly REMEMBER_ME_KEY = 'patient_portal_credentials';

  constructor(
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private router: Router,
    private route: ActivatedRoute,
    private appStateService: AppStateService,
    private formConfigService: FormConfigService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private appointmentService: AppointmentService,
    private deeplinkService: DeeplinkService
  ) {
    this.clientId = this.appStateService.getState().clientId || '';
    this.orgId = this.appStateService.getState().orgId || '';
    this.authForm = this.fb.group({
      phoneNumber: ['', [Validators.required, CustomValidators.phoneNumber]],
      dob: ['', [Validators.required]],
      rememberMe: [false]
    });

    // Initialize name verification form
    this.nameVerificationForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]]
    });
  }

  ngOnInit(): void {
    if (!this.clientId) {
      this.snackBar.open('Client ID not found', 'Close', { duration: 3000 });
    }
    this.appConfig = this.configService.getConfigData();

    // Clear any stale session data when landing on login page
    // This ensures fresh login instead of using cached patient IDs
    this.clearSessionData();

    // Check for query parameters from signup redirect and pre-fill form
    this.route.queryParams.subscribe(params => {
      if (params['phone']) {
        this.authForm.patchValue({ phoneNumber: params['phone'] });
      }
      if (params['dob']) {
        // Parse the date - it might come as YYYY-MM-DD or MM/DD/YYYY
        let dobDate: Date | null = null;
        
        if (params['dob'].includes('-')) {
          // Format: YYYY-MM-DD
          dobDate = new Date(params['dob']);
        } else if (params['dob'].includes('/')) {
          // Format: MM/DD/YYYY
          const parts = params['dob'].split('/');
          if (parts.length === 3) {
            const month = parseInt(parts[0], 10) - 1; // Month is 0-indexed
            const day = parseInt(parts[1], 10);
            const year = parseInt(parts[2], 10);
            dobDate = new Date(year, month, day);
          }
        }
        
        if (dobDate && !isNaN(dobDate.getTime())) {
          this.authForm.patchValue({ dob: dobDate });
        }
      }
    });

    // Load saved credentials if available (but don't override query params)
    this.loadSavedCredentials();

    // Fetch clinic info using public endpoint to have phone number available for error messages
    if (this.clientId && this.orgId) {
      this.formConfigService.fetchPublicClinicInfo(this.orgId, this.clientId).subscribe({
        next: (clinicData: ClinicInformation) => {
          this.clinicInfo = clinicData;
          this.appStateService.setState({ clinicInfo: this.clinicInfo });
        },
        error: (error) => {
        }
      });
    }
    // get authentication token if available from local storage
    const idToken = sessionStorage.getItem('idToken');
    if (idToken == 'anonymous') {
      this.authService.logout();
      return;
    }
    const patientId = sessionStorage.getItem('patientId');
    if (idToken && idToken !== '' && patientId && patientId !== '') {
      this.router.navigate(['/dashboard']);
    }
  }

  ngAfterViewInit(): void {
    // Focus the appropriate field after view is initialized
    setTimeout(() => {
      this.setInitialFocus();
    }, 100);
  }

  private async loadSavedCredentials(): Promise<void> {
    const savedData = await StorageEncryptionUtil.getItem(this.REMEMBER_ME_KEY);

    if (savedData && savedData.phoneNumber) {
      this.authForm.patchValue({
        phoneNumber: savedData.phoneNumber,
        rememberMe: true
      });
    }
  }

  private async saveCredentials(): Promise<void> {
    const rememberMe = this.authForm.get('rememberMe')?.value;

    if (rememberMe) {
      const phoneNumber = this.authForm.get('phoneNumber')?.value;

      // Only store phone number, not DOB for security
      await StorageEncryptionUtil.setItem(this.REMEMBER_ME_KEY, {
        phoneNumber: phoneNumber
      }, 30); // 30 days expiry
    } else {
      // Remove saved credentials if Remember Me is unchecked
      StorageEncryptionUtil.removeItem(this.REMEMBER_ME_KEY);
    }
  }

  private clearSessionData(): void {
    try {
      // Clear all session data to prevent stale patient IDs
      sessionStorage.removeItem('patientId');
      sessionStorage.removeItem('idToken');
      sessionStorage.removeItem('phoneNumber');
    } catch (error) {
    }
  }


  private setInitialFocus(): void {
    // If phone number is already filled (from Remember Me), focus on DOB field
    const phoneValue = this.authForm.get('phoneNumber')?.value;
    if (phoneValue && phoneValue !== '' && this.dobInput?.nativeElement) {
      this.dobInput.nativeElement.focus();
    } else if (this.phoneInput?.nativeElement) {
      // Otherwise, focus on phone number field
      this.phoneInput.nativeElement.focus();
    }
  }

   /* Handling Image Load Errors */
   onImgError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/client-logo.png';
  }
  
  getLogoUrl(): string {
    // Validate the logo URL before using it
    if (this.appConfig.logoUrl) {
      const trimmedUrl = this.appConfig.logoUrl.trim();
      if (trimmedUrl && 
          (trimmedUrl.startsWith('http://') || 
           trimmedUrl.startsWith('https://') ||
           trimmedUrl.startsWith('assets/') ||
           trimmedUrl.startsWith('/'))) {
        return trimmedUrl;
      }
    }
    return 'assets/client-logo.png';
  }

  showRememberMeInfo(): void {
    this.dialog.open(RememberMeInfoDialogComponent, {
      width: '400px',
      panelClass: 'remember-me-dialog'
    });
  }

  getContextInfo() {
    const redirectUrl = this.appStateService.getState().redirectUrl;
    if (redirectUrl && redirectUrl !== '') {
       if (redirectUrl.startsWith('forms/new_patient_registration')) {
         return 'Login for New Patient Registration';
       }
       if (redirectUrl.startsWith('consent')) {
         return 'Login for Consent Form';
       }
       if (redirectUrl.startsWith('appointment')) {
         return 'Login for Appointment Scheduling';
       }
       if (redirectUrl.startsWith('payments')) {
         return 'Login for Payment';
       }
    }
    return '';
  }

  authenticate(): void {
    if (this.requestInProgress) {
      return;
    }

    this.requestInProgress = true;

    // Save credentials if Remember Me is checked
    this.saveCredentials();

    const phoneNumber = this.formatPhoneNumber(this.authForm.get('phoneNumber')?.value);
    const dob = this.formatDateOfBirth(this.authForm.get('dob')?.value);

    this.formConfigService.fetchPatientFamilyLookupAnonymous(this.clientId, {
      orgId: this.orgId,
      clientId: this.clientId,
      phoneNumber: phoneNumber,
      dob: dob
    }).subscribe({
      next: (response: any) => {
        // Backend now always returns array
        if (!Array.isArray(response)) {
          this.requestInProgress = false;
          this.handleNoPatientFound();
          return;
        }

        if (response.length === 0) {
          // No patient found
          this.requestInProgress = false;
          this.handleNoPatientFound();
        } else if (response.length === 1) {
          // Single match - proceed to send OTP
          this.proceedWithPatient(response[0], phoneNumber);
        } else {
          // Multiple matches - show name verification dialog
          this.requestInProgress = false;
          this.showPatientSelector(response, phoneNumber);
        }
      },
      error: (error: any) => {

        // Check if there's appointment data and patient type is new-emergency
        const appointmentData = this.appStateService.getState().appointmentData;
        const patientType = appointmentData?.patientType;

        if (patientType === 'new-emergency') {
          // Phone number not found and it's a new patient - redirect to registration
          this.snackBar.open('No existing account found. Let\'s create a new patient profile.', 'Close', { duration: 4000 });
          this.requestInProgress = false;
          this.router.navigate(['/signup'], {
            queryParams: {
              phone: this.authForm.get('phoneNumber')?.value,
              dob: this.authForm.get('dob')?.value
            }
          });
          return;
        }

        // For existing patient types, show error message
        let errorMessage = 'No patient record found with the provided information. ';
        if (this.clinicInfo?.phoneNumber) {
          errorMessage += `Please contact your healthcare provider at ${this.clinicInfo.phoneNumber} to verify your account.`;
        } else {
          errorMessage += 'Please contact your healthcare provider to verify your account.';
        }
        this.snackBar.open(errorMessage, 'Close', { duration: 6000 });
        this.requestInProgress = false;
      }
    });
  }



  private formatPhoneNumber(phone: string): string {
    phone = phone.replace(/\D/g, '');
    if (phone.length === 10) {
      phone = '+1' + phone;
    } else if (phone.length === 11 && phone.startsWith('1')) {
      phone = '+' + phone;
    }
    return phone;
  }

  private formatDateOfBirth(dob: string | Date): string {
    // Handle both string and Date inputs
    let date: Date;

    if (typeof dob === 'string') {
      date = new Date(dob);
    } else {
      date = dob;
    }

    // Use local date methods to avoid timezone offset issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  newPatient(event: Event): void {
    event.preventDefault();
    this.isNewPatient = true;
    this.router.navigate(['/new-patient']);
  }

  onOtpBack(): void {
    this.showOtpInput = false;
    this.otpVerifyInProgress = false;
  }

  onOtpVerified(idToken: string): void {
    if (!idToken || idToken === '') {
      return;
    }

    this.patientId = sessionStorage.getItem('patientId') || '';
    if (!this.patientId || this.patientId === '') {
      this.authService.logout();
      return;
    }

    sessionStorage.setItem('idToken', idToken);
    this.authService.login(this.patientId, idToken).subscribe({
      next: (response: any) => {
        this.authService.setToken(response.access_token);
        this.authService.setRefreshToken(response.refresh_token);
        this.appStateService.setState({
          patientId: this.patientId,
        });

        // Check if there's a pending appointment to book
        const appointmentData = this.appStateService.getState().appointmentData;
        if (appointmentData) {
          // User came from scheduler with selected slot - auto-book it
          this.bookPendingAppointment(appointmentData);
        } else {
          // Check for deep link context
          const deeplinkContext = this.deeplinkService.getDeeplinkContext();
          if (deeplinkContext && deeplinkContext.url) {
            // Clear deep link context before navigation to prevent duplicate handling
            this.deeplinkService.clearDeeplinkContext();
            this.router.navigateByUrl(deeplinkContext.url);
          } else {
            // Check for redirectUrl in app state
            const redirectUrl = this.appStateService.getState().redirectUrl;
            if (redirectUrl) {
              this.router.navigate(['/dashboard', ...redirectUrl.split('/')]);
            } else {
              // Default to dashboard
              this.router.navigate(['/dashboard']);
            }
          }
        }
      },
      error: (error: any) => {
        this.snackBar.open('Login failed', 'Close', { duration: 3000 });
        this.authService.logout();
      }
    });
  }

  private bookPendingAppointment(appointmentData: any): void {
    const clientId = this.appStateService.getState().clientId;
    const orgId = this.appStateService.getState().orgId;

    const bookingPayload = {
      clientId: clientId,
      orgId: orgId,
      patientId: this.patientId,
      appointmentDateTime: appointmentData.appointmentDateTime,
      appointmentTypeNumber: appointmentData.appointmentTypeNumber,
      operatoryNumber: appointmentData.operatoryNumber,
      lengthInMinutes: appointmentData.lengthInMinutes,
      summary: appointmentData.summary || '',
      isNewPatient: appointmentData.isNewPatient,
      patientType: appointmentData.patientType
    };

    this.snackBar.open('Booking your appointment...', '', { duration: 2000 });

    this.appointmentService.createAppointment(bookingPayload).subscribe({
      next: (response: any) => {

        this.snackBar.open('Appointment booked successfully!', 'Close', { duration: 5000 });

        this.appStateService.setState({
          redirectUrl: '',
          appointmentBooked: true
        });

        this.router.navigate(['/dashboard/appointment', 'new']);
      },
      error: (error: any) => {
        this.snackBar.open('Failed to book appointment. Please try again from the dashboard.', 'Close', { duration: 5000 });

        this.router.navigate(['/dashboard']);
      }
    });
  }

  navigateToSignup(): void {
    // Navigate to signup page with phone and DOB if available
    const queryParams: any = {};

    const phoneNumber = this.authForm.get('phoneNumber')?.value;
    const dob = this.authForm.get('dob')?.value;

    if (phoneNumber) {
      queryParams.phone = phoneNumber;
    }

    if (dob) {
      queryParams.dob = dob;
    }

    this.router.navigate(['/signup'], { queryParams });
  }

  navigateToKiosk(): void {
    this.router.navigate(['/kiosk']);
  }

  private proceedWithPatient(patient: any, phoneNumber: string): void {
    this.patientId = patient.id;
    sessionStorage.setItem('patientId', this.patientId);
    this.appStateService.setState({
      patientId: this.patientId,
      phoneNumber: phoneNumber,
    });

    // Show OTP input and auto-send OTP
    this.phoneNumberForOtp = phoneNumber;
    this.showOtpInput = true;
    this.otpVerifyInProgress = true;
    this.requestInProgress = false;
  }

  private showPatientSelector(patients: any[], phoneNumber: string): void {
    // Show inline name verification form instead of dialog
    this.pendingPatients = patients;
    this.pendingPhoneNumber = phoneNumber;
    this.showNameVerification = true;
    this.nameVerificationError = '';
    this.nameVerificationForm.reset();
  }

  verifyName(): void {
    if (this.nameVerificationForm.invalid) {
      return;
    }

    const firstName = this.nameVerificationForm.get('firstName')?.value?.trim().toLowerCase();
    const lastName = this.nameVerificationForm.get('lastName')?.value?.trim().toLowerCase();

    // Find matching patient by name (case-insensitive)
    const matchedPatient = this.pendingPatients.find(p =>
      p.firstName?.toLowerCase() === firstName &&
      p.lastName?.toLowerCase() === lastName
    );

    if (matchedPatient) {
      this.showNameVerification = false;
      this.nameVerificationError = '';
      this.proceedWithPatient(matchedPatient, this.pendingPhoneNumber);
    } else {
      this.nameVerificationError = 'No matching account found. Please check your name and try again.';
    }
  }

  cancelNameVerification(): void {
    this.showNameVerification = false;
    this.pendingPatients = [];
    this.pendingPhoneNumber = '';
    this.nameVerificationError = '';
    this.nameVerificationForm.reset();
  }

  private handleNoPatientFound(): void {
    const appointmentData = this.appStateService.getState().appointmentData;
    const patientType = appointmentData?.patientType;

    // Get the phone and DOB values from the login form
    const phoneNumber = this.authForm.get('phoneNumber')?.value;
    const dateOfBirth = this.authForm.get('dob')?.value;

    if (patientType === 'new-emergency') {
      // Phone number not found and it's a new patient - redirect to registration
      this.snackBar.open('No existing account found. Let\'s create a new patient profile.', 'Close', { duration: 4000 });
      this.router.navigate(['/signup'], {
        queryParams: {
          phone: phoneNumber,
          dob: dateOfBirth
        }
      });
    } else {
      // For existing patient types, show error message
      let errorMessage = 'No patient record found. Would you like to create a new account?';
      
      const snackBarRef = this.snackBar.open(errorMessage, 'Create Account', { duration: 8000 });
      
      snackBarRef.onAction().subscribe(() => {
        this.router.navigate(['/signup'], {
          queryParams: {
            phone: phoneNumber,
            dob: dateOfBirth
          }
        });
      });
    }
  }
}
